import { Button, Card, Col, Progress, Row, Grid, Spin, Skeleton } from "antd";
import { useState } from "react";
import { fourth_systemInformation } from "../../configs/configs";
import LineBarChart from "../common/LineBarChart";
import flexibleFormat from "../../functions/flexibleFormat";

const { useBreakpoint } = Grid;

/** Card that shows a stacked chart showing proportions of emission and abatement for the given scope, year and base year;
 * and also shows a list of the emission/abatement sources
 * */
export const BreakdownCard = (props) => {
  const { title, chartContainerName, year, baseYear, loading } = props;
  const { emissionSummary, emissionScopeData, abatementData } = props;
  // The JS object keys for each scope are manually specified
  const { emissionSummaryKeys } = props;

  const [button, setButton] = useState("TotalEmission");

  const screens = useBreakpoint();

  let totalEmission = 0;
  let totalAbatement = 0;

  // Initialize chart data to get fixed order and guarantee there is at least zero in data
  let barData = [
    {
      year: baseYear.toString(),
      value: 0,
      type: "Abatement",
    },
    {
      year: year.toString(),
      value: 0,
      type: "Abatement",
    },
    {
      year: baseYear.toString(),
      value: 0,
      type: "Net Emission",
    },
    {
      year: year.toString(),
      value: 0,
      type: "Net Emission",
    },
  ];
  // Initialize line chart data
  let lineData = [
    {
      year: baseYear.toString(),
      value: 0,
      name: "Total Emission",
    },
    {
      year: year.toString(),
      value: 0,
      name: "Total Emission",
    },
  ];

  emissionSummary
    .sort((a, b) => a.InventoryYear - b.InventoryYear)
    .forEach((inventory) => {
      if (inventory.InventoryYear === year.toString()) {
        // Sum the values from different keys within the JS object
        emissionSummaryKeys.forEach((key) => {
          inventory[key] && (totalEmission += inventory[key]);
        });
      }

      // Insert data to net emission with same year
      let idx = barData.findIndex(
        (element) =>
          element.year === inventory.InventoryYear.toString() &&
          element.type === "Net Emission"
      );
      if (idx !== -1) {
        emissionSummaryKeys.forEach((key) => {
          inventory[key] && (barData[idx].value += inventory[key]);
        });
      }

      // Insert data to emission with same year
      idx = lineData.findIndex(
        (element) =>
          element.year.toString() === inventory.InventoryYear.toString()
      );
      if (idx !== -1) {
        emissionSummaryKeys.forEach((key) => {
          inventory[key] && (lineData[idx].value += inventory[key]);
        });
      }
    });

  abatementData.forEach((element) => {
    if (element.Year === year.toString()) {
      totalAbatement += element.Quantity * element.EmissionFactor;
    }
    let idx = barData.findIndex(
      (chart) =>
        chart.year === element.Year.toString() && chart.type === "Abatement"
    );
    if (idx !== -1) {
      barData[idx].value += element.Quantity * element.EmissionFactor;
    }

    idx = barData.findIndex(
      (chart) =>
        chart.year === element.Year.toString() && chart.type === "Net Emission"
    );
    if (idx !== -1) {
      barData[idx].value -= element.Quantity * element.EmissionFactor;
    }
  });

  const netEmission = totalEmission - totalAbatement;

  const breakdownList = () => {
    let items = [];
    // Sum up values of items with similar names together
    if (button === "TotalEmission") {
      emissionScopeData
        .filter((element) => element.InventoryYear === year.toString())
        .forEach((element) => {
          let idx = items.findIndex(
            (value) =>
              value.name.toLocaleLowerCase() ===
              element.ActiveName.toLocaleLowerCase()
          );

          if (idx !== -1) {
            items[idx].value += element.TotalCO2e;
            items[idx].percent = (
              (items[idx].value / totalEmission) *
              100
            ).toFixed(2);
          } else {
            items.push({
              name: element.ActiveName,
              value: element.TotalCO2e,
              percent: ((element.TotalCO2e / totalEmission) * 100).toFixed(2),
            });
          }
        });
    } else {
      // Abatement data
      abatementData
        .filter((element) => element.Year === year.toString())
        .forEach((element) => {
          let idx = items.findIndex(
            (value) =>
              value.name.toLocaleLowerCase() ===
              element.Name.toLocaleLowerCase()
          );

          if (idx !== -1) {
            items[idx].value += element.Quantity * element.EmissionFactor;
            items[idx].percent = flexibleFormat(
              (items[idx].value / totalAbatement) * 100
            );
          } else {
            items.push({
              name: element.Name,
              value: element.Quantity * element.EmissionFactor,
              percent: flexibleFormat(
                ((element.Quantity * element.EmissionFactor) / totalAbatement) *
                  100
              ),
            });
          }
        });
    }

    items = items.sort((a, b) => b.value - a.value);
    return items.map((item) => {
      return (
        <Row
          style={{
            minHeight: "2rem",
            width: "100%",
            textAlign: "left",
            padding: "6px 0px",
          }}
        >
          <Col
            span={12}
            style={{
              height: "100%",
              width: "100%",
              padding: "0px 5px",
            }}
          >
            <h4
              style={{
                margin: "0px",
                fontWeight: "400",
                fontSize: "13px",
              }}
            >
              {item.name}
            </h4>
          </Col>
          <Col
            span={12}
            style={{
              height: "100%",
              width: "100%",
              padding: "0px 5px",
            }}
          >
            <h4
              style={{
                margin: "0px",
                fontWeight: "400",
                fontSize: "13px",
              }}
            >
              {item.value.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{" "}
              <span style={{ fontSize: 10 }}>
                tCO<sub>2</sub>e
              </span>{" "}
              ({item.percent}%)
            </h4>
          </Col>
        </Row>
      );
    });
  };

  return (
    <Card
      size="small"
      title={
        <Row>
          <Col sm={24} md={16}>
            <h5 style={{ margin: "0px", fontSize: "17.6px" }}>{title}</h5>
          </Col>
          <Col
            sm={24}
            md={8}
            style={{ width: "100%", padding: screens.md ? "0px" : "8px 4px" }}
          >
            <Row style={{ width: "100%", marginTop: "-4px" }}>
              <Col span={11} style={{ width: "100%" }}>
                <h3
                  style={{
                    margin: "0px",
                    height: "100%",
                    width: "100%",
                    fontSize: "13px",
                  }}
                >
                  Net Emission
                </h3>
              </Col>
              <Col span={13} style={{ width: "100%", textAlign: "right" }}>
                <Row style={{ width: "100%", height: "0.5vh" }}>
                  <Col span={24} style={{ width: "100%" }}>
                    <h3
                      style={{
                        margin: "0px",
                        height: "100%",
                        width: "100%",
                        fontSize: "13px",
                      }}
                    >
                      {netEmission.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      tCO<sub>2</sub>e
                    </h3>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ width: "100%", marginTop: "-11px" }}>
              <Col span={24} style={{ width: "100%" }}>
                <Progress
                  percent={(netEmission / totalEmission) * 100}
                  showInfo={false}
                  strokeColor={"rgba(122, 123, 173, 1)"}
                  strokeLinecap="square"
                  status="normal"
                  trailColor={fourth_systemInformation}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      }
      style={{ width: "100%", height: screens.xl ? "100%" : "auto" }}
    >
      <Row style={{ width: "100%" }}>
        <Col
          sm={24}
          md={11}
          style={{ height: "20vh", width: "100%", minHeight: "180px" }}
        >
          {loading && (
            <div
              style={{
                width: "100%",
                height: "100%",
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Spin />
            </div>
          )}
          <LineBarChart
            barData={barData}
            lineData={lineData}
            xBar="year"
            yBar="value"
            xLine="year"
            yLine="value"
            colorBar="type"
            colorLine="name"
            style={{
              height: "100%",
              width: "100%",
              padding: "8px 20px",
              display: loading ? "none" : "block",
            }}
            yAxisTitle={"Total CO2 (tCO2e)"}
            labelFontSize={12}
            showXAxisTitle={false}
          />
        </Col>
        <Col
          sm={24}
          md={13}
          style={{
            width: "100%",
            padding: "10px 12px 10px 12px",
          }}
        >
          <div style={{ width: "100%" }}>
            <Row style={{}}>
              <Col span={12} style={{ width: "100%" }}>
                <Button
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "5px",
                    width: "100%",
                    textAlign: "center",
                  }}
                  type={button === "TotalEmission" ? "primary" : "default"}
                  onClick={() => {
                    setButton("TotalEmission");
                  }}
                >
                  Total Emission
                </Button>
              </Col>
              <Col span={12} style={{ width: "100%" }}>
                <Button
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "5px",
                    width: "100%",
                    textAlign: "center",
                  }}
                  type={button === "Abatement" ? "primary" : "default"}
                  onClick={() => {
                    setButton("Abatement");
                  }}
                >
                  Abatement
                </Button>
              </Col>
            </Row>
            <Row style={{ height: "12vh", width: "100%", overflowY: "auto" }}>
              <Col span={24}>
                {loading && (
                  <div
                    style={{ width: "50%", height: "10vh", paddingTop: "8px" }}
                  >
                    <Skeleton active />
                  </div>
                )}
                {!loading && breakdownList()}
              </Col>
            </Row>
            <Row style={{ width: "100%" }}>
              <Col span={24}>
                <Row
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "3px 0px",
                  }}
                >
                  <Col
                    span={12}
                    style={{
                      width: "100%",
                      padding: "0px 5px",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0px",
                        fontWeight: "500",
                        fontSize: "13px",
                      }}
                    >
                      {button === "Abatement"
                        ? "Total Abatement"
                        : "Total Emission"}
                    </h4>
                  </Col>
                  <Col
                    span={12}
                    style={{
                      width: "100%",
                      padding: "0px 5px",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0px",
                        fontWeight: "500",
                        fontSize: "13px",
                      }}
                    >
                      {(button === "Abatement"
                        ? totalAbatement
                        : totalEmission
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      tCO<sub>2</sub>e
                    </h4>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Card>
  );
};
